import { createSlice } from '@reduxjs/toolkit';
import { sortByField } from 'utils/helpers';
import { Basket } from 'types/common';
import { BasketActivityUser } from 'types/baskets';
import { createAsyncThunkWrapper } from 'redux/thunk/createAsyncThunkWrapper';
import { fetchParishBasketDetail, fetchBasketContributions } from 'services/basketsApi';
import { selectSelectedParishId } from 'redux/selectors/parishContext';

interface typesOfBasketDetails {
    basketDetails: Basket | null;
    activityDetailData: BasketActivityUser[];
    loading: boolean;
    error: string | null;
}
const initialState: typesOfBasketDetails = {
    basketDetails: null,
    activityDetailData: [],
    loading: false,
    error: null,
};

/**
 * Remove-bucket: wallet withdraw is gone. Keep thunk as a no-op error so any
 * leftover caller fails loudly instead of hitting legacy /event/withdraw.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing
export const transferBasketBalance = createAsyncThunkWrapper<any, { id: string }>('basketDetails/transferBasketBalance', async () => {
    throw new Error('Basket withdraw is not available on the new backend');
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing
export const fetchBasketDetail = createAsyncThunkWrapper<any, string>(
    'basketDetails/fetchBasketDetail',
    async (id: string, { getState }) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing
        const parishId = selectSelectedParishId(getState() as any);
        if (!parishId) throw new Error('No selected parish');
        const event = await fetchParishBasketDetail(parishId, String(id));
        return { event };
    }
);

/** GET .../contributions — single page of up to 100, most recent first (no pagination UI yet). */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing
export const fetchActivityDetail = createAsyncThunkWrapper<any, string | undefined>(
    'activityDetails/fetchActivityDetail',
    async (id, { getState }) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing
        const parishId = selectSelectedParishId(getState() as any);
        if (!parishId || !id) return { payments: [] as BasketActivityUser[] };
        const { items } = await fetchBasketContributions(parishId, id);
        return { payments: items };
    }
);

const basketDetailSlice = createSlice({
    name: 'basketDetailsActivity',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(fetchBasketDetail.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(fetchBasketDetail.fulfilled, (state, action) => {
            state.loading = false;
            if (action?.payload) {
                const { event } = action.payload;
                state.basketDetails = event || null;
            }
        });
        builder.addCase(fetchBasketDetail.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message || null;
        });

        builder.addCase(fetchActivityDetail.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(fetchActivityDetail.fulfilled, (state, action) => {
            state.loading = false;
            if (action.payload) {
                const { payments } = action.payload;
                state.activityDetailData = sortByField(payments, 'createdAt', 'DESC') || [];
            }
        });
        builder.addCase(fetchActivityDetail.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message || null;
        });
    },
});
export default basketDetailSlice.reducer;
